import { useState } from 'react';
import { Avatar, Button, Space, Tooltip, Typography, theme } from 'antd';
import { BankOutlined, FieldTimeOutlined } from '@ant-design/icons';
import { DataTable } from '../../../components/DataTable';
import { EmptyState } from '../../../components/EmptyState';
import { VisitaResultadoTag } from '../../visitas/components/VisitaResultadoTag';
import { PagoCreateModal } from '../../pagos/components/PagoCreateModal';
import { VisitaCreateModal } from '../../visitas/components/VisitaCreateModal';
import { useCobradorClientes, useInvalidarCobrador } from '../api/useCobrador';
import { useT } from '../../../locales';
import { DATE_FORMAT, parseDateOnly } from '../../../lib/date';
import {
  formatCurrency,
  type ClienteRef,
  type CobradorClienteRow,
  type SeccionCobrador,
} from '@aid/shared';
import { serverSortProps, useServerPaginacion } from '../../../lib/server-sort';

const { Text } = Typography;

interface CobradorClientesTableProps {
  seccion: SeccionCobrador;
}

function getInitials(nombre: string | null | undefined): string {
  const words = nombre?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (words.length === 0) return '#';
  return words
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

function toClienteRef(cliente: CobradorClienteRow): ClienteRef {
  return {
    id: cliente.idCliente,
    nombre: cliente.nombre,
    identificacion: null,
    ip: cliente.codigo,
  };
}

export function CobradorClientesTable({ seccion }: CobradorClientesTableProps) {
  const t = useT();
  const { token } = theme.useToken();
  const { page, pageSize, sort, handleChange } = useServerPaginacion<CobradorClienteRow>(20);
  const [cobrarCliente, setCobrarCliente] = useState<CobradorClienteRow | null>(null);
  const [visitarCliente, setVisitarCliente] = useState<CobradorClienteRow | null>(null);
  const refrescar = useInvalidarCobrador();

  const { data, isLoading, error } = useCobradorClientes(seccion, {
    limit: pageSize,
    offset: (page - 1) * pageSize,
    ...sort,
  });

  if (error) {
    return <EmptyState title={t('pages.cobrador.errorLoad')} />;
  }

  const columns = [
    {
      title: t('pages.cobrador.table.cliente'),
      key: 'cliente',
      ...serverSortProps('cliente', sort),
      render: (_: unknown, record: CobradorClienteRow) => (
        <Space size={8}>
          <Avatar size={24} style={{ background: token.colorPrimary, fontSize: 12 }} aria-hidden>
            {getInitials(record.nombre)}
          </Avatar>
          <span>
            {record.nombre ?? `#${record.idCliente}`}
            {record.codigo && (
              <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                {record.codigo}
              </Text>
            )}
          </span>
        </Space>
      ),
    },
    {
      title: t('pages.cobrador.table.deudaTotal'),
      dataIndex: 'deudaTotal' as keyof CobradorClienteRow,
      key: 'deudaTotal',
      ...serverSortProps('deudaTotal', sort),
      align: 'right' as const,

      render: (val: string) => formatCurrency(val),
    },
    {
      title: t('pages.cobrador.table.facturas'),
      dataIndex: 'facturasPendientes' as keyof CobradorClienteRow,
      key: 'facturasPendientes',
      ...serverSortProps('facturasPendientes', sort),
      align: 'right' as const,

      render: (val: number) => val,
    },
    {
      title: t('pages.cobrador.table.ultimaVisita'),
      key: 'ultimaVisita',
      ...serverSortProps('ultimaVisita', sort),
      render: (_: unknown, record: CobradorClienteRow) =>
        record.ultimaVisita ? (
          <Space size={6}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {parseDateOnly(record.ultimaVisita.fecha)?.format(DATE_FORMAT) ?? '—'}
            </Text>
            <VisitaResultadoTag
              resultado={
                record.ultimaVisita.resultado as Parameters<
                  typeof VisitaResultadoTag
                >[0]['resultado']
              }
            />
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_: unknown, record: CobradorClienteRow) => (
        <Space size={4}>
          <Tooltip title={t('pages.cobrador.table.cobrar')}>
            <Button
              type="primary"
              size="small"
              icon={<BankOutlined />}
              aria-label={t('pages.cobrador.table.cobrar')}
              onClick={() => setCobrarCliente(record)}
            >
              {t('pages.cobrador.table.cobrar')}
            </Button>
          </Tooltip>
          <Tooltip title={t('pages.cobrador.table.visita')}>
            <Button
              type="text"
              size="small"
              icon={<FieldTimeOutlined />}
              aria-label={t('pages.cobrador.table.visita')}
              onClick={() => setVisitarCliente(record)}
            >
              {t('pages.cobrador.table.visita')}
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <DataTable<CobradorClienteRow>
        columns={columns}
        dataSource={data?.data}
        rowKey="idCliente"
        loading={isLoading}
        locale={{
          emptyText: (
            <EmptyState
              title={t('pages.cobrador.emptyTitle')}
              description={t('pages.cobrador.emptyDescription')}
            />
          ),
        }}
        pagination={{
          current: page,
          pageSize,
          total: data?.total ?? 0,
          showSizeChanger: false,
          showTotal: (total, range) =>
            t('pagination.showTotal', { from: range[0], to: range[1], total }),
        }}
        onChange={handleChange}
      />
      <PagoCreateModal
        open={cobrarCliente !== null}
        preselectClienteId={cobrarCliente?.idCliente}
        preselectedCliente={cobrarCliente ? toClienteRef(cobrarCliente) : undefined}
        onClose={() => setCobrarCliente(null)}
        onSuccess={refrescar}
      />
      <VisitaCreateModal
        open={visitarCliente !== null}
        preselectClienteId={visitarCliente?.idCliente}
        preselectedCliente={visitarCliente ? toClienteRef(visitarCliente) : undefined}
        onClose={() => setVisitarCliente(null)}
        onSuccess={refrescar}
      />
    </div>
  );
}
